/**
 * Use this file for JavaScript code that you want to run in the front-end
 * on posts/pages that contain this block.
 *
 * When this file is defined as the value of the `viewScript` property
 * in `block.json` it will be enqueued on the front end of the site.
 *
 * Example:
 *
 * ```js
 * {
 *   "viewScript": "file:./view.js"
 * }
 * ```
 *
 * If you're not making any changes to this file because your project doesn't need any
 * JavaScript running in the front-end, then you should delete this file and remove
 * the `viewScript` property from `block.json`.
 *
 * @see https://developer.wordpress.org/block-editor/reference-guides/block-api/block-metadata/#view-script
 */

/**
 * Marquee Class
 * Dynamically handles an infinite scrolling loop for any number of images.
 */
class Marquee {
	constructor() {
		this.root = document.documentElement;
		this.marqueeContent = document.querySelector(
			".collaborators__marquee-list",
		);

		// Only initialize if the marquee exists and has items
		if (this.marqueeContent && this.marqueeContent.children.length > 0) {
			this.setup();
		}
	}

	setup() {
		// 1. Capture the original items before we start cloning
		this.originalItems = Array.from(this.marqueeContent.children);
		const originalCount = this.originalItems.length;

		// 2. Set the dynamic count in CSS (replaces hardcoded value)
		this.root.style.setProperty("--marquee-elements", originalCount);

		// 3. Get the display count from your SCSS variable
		this.marqueeElementsDisplayed =
			parseInt(
				getComputedStyle(this.root).getPropertyValue(
					"--marquee-elements-displayed",
				),
			) || 5;

		this.init();
	}

	init() {
		// 4. Append clones to create the "buffer" for a seamless loop
		// We clone enough items to fill the visible screen area
		for (let i = 0; i < this.marqueeElementsDisplayed; i++) {
			const clone =
				this.originalItems[i % this.originalItems.length].cloneNode(true);
			clone.setAttribute("aria-hidden", "true");
			this.marqueeContent.appendChild(clone);
		}

		// 5. Start the animation after the DOM has updated with clones
		this.startAnimation();
	}

	startAnimation() {
		const firstItem = this.marqueeContent.querySelector(
			".collaborators__marquee-item",
		);
		if (!firstItem) return;

		// Get the actual rendered pixel width (includes padding/borders)
		const itemWidth = firstItem.offsetWidth;

		// Total distance to scroll is: (width of 1 item) * (number of original items)
		const totalDistance = itemWidth * this.originalItems.length;

		// Duration: 3000ms (3s) multiplied by the number of original items
		const duration = this.originalItems.length * 3000;

		// Use Web Animations API for a pixel-perfect reset
		const animation = this.marqueeContent.animate(
			[
				{ transform: "translateX(0px)" },
				{ transform: `translateX(-${totalDistance}px)` },
			],
			{
				duration: duration,
				iterations: Infinity,
				easing: "linear",
			},
		);

		// Pause on hover
		this.marqueeContent.addEventListener("mouseenter", () => animation.pause());
		this.marqueeContent.addEventListener("mouseleave", () => animation.play());
	}
}

// Ensure the DOM is fully loaded before running the script
window.addEventListener("DOMContentLoaded", () => {
	new Marquee();
});
